import React, { useState, useEffect } from "react";
import {
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  Typography,
  Box,
  TextField,
  Pagination,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import Icon from "../../Assets/images/svg/Icons/CategorieIcon.png";
import ActionIcon from "../../Assets/images/png/Action.png";
import SearchandExport from "../../Components/Common/SearchandExport/SearchandExport";

import BoxHeader from "../../Components/Common/Box/Boxheader";
import "../../Components/Common/Styles/Formstyles.css";
import {
  PostApiMethod,
  PatchApiMethod,
  DeleteApiMethod,
  GetApiMethod,
} from "../../Utils/ApiService";
import { CategoryEndpoint } from "../../Api/Api";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SuccessDialog from "../../Components/Common/SuccesMessages/SuccessDialog";
import { useSuccessDialog } from "../../Components/Common/SuccesMessages/SuccessDialog";
import {
  Category as ContextCategory,
  useCategory,
} from "../../Components/Common/Context/Context";
import CategoryPhotosForm from "./CategoryPhotosForm";

export interface Category {
  id: string;
  name: string;
  label: string;
  providerId: string;
  photos?: string[];
}
export interface LocalCategory extends ContextCategory {
  label?: string;
  providerId?: string;
  imageUrl?: string;
  fileKey?: string;
}

const Categories: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filteredCategories, setFilteredCategories] = useState<LocalCategory[]>([]);
  const [showCategoryList, setShowCategoryList] = useState<boolean>(true);
  const [newCategoryName, setNewCategoryName] = useState<string>("");
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [touched, setTouched] = useState(false);
  // const [, setIsDisabled] = useState(false);
  const [formData, setFormData] = useState<any>({ photos: [] });
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const { id: categoryIdFromParams } = useParams();
  const routerLocation = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const { allCategories, setAllCategories, fetchCategories } = useCategory();
  const [loading, setLoading] = useState(false);
  const { open: successOpen, message, showSuccess, closeSuccess } = useSuccessDialog();

  // from context
  const { selectedCategoryId, setSelectedCategoryId } = useCategory();
  const fetchCategoryById = async (catId: string, forEdit = false) => {
    
    try {
      setLoading(true);
      const res = await GetApiMethod(`category/${catId}?_=${Date.now()}`);
      const category = res?.data?.data?.category;

      if (category) {
        // Map photos to full URLs if needed
        // const photos = (category.photos || []).map((p: string) =>
        //   p.startsWith("http") ? p : `${process.env.REACT_APP_API_URL}/${p}`
        // );

        if (forEdit) {
          setNewCategoryName(category.name || "");

          // Use existing image URL as initial photo
          const initialPhotos = category.imageUrl ? [category.imageUrl] : [];
          setFormData({ photos: initialPhotos });

          setEditingCategoryId(category.id);
          setShowCategoryList(false);
        } else {
          setFilteredCategories([category]);
          setShowCategoryList(true);
        }
      } else {
        setFilteredCategories([]);
        setShowCategoryList(true);
      }
    } catch (error) {
      console.error("Error fetching category:", error);
      setFilteredCategories([]);
      setShowCategoryList(true);
    } finally {
      setLoading(false);
    }
  };


  // filter when search or allCategories changes
 useEffect(() => {
  setLoading(true);

  const timeout = setTimeout(() => {
    const results = allCategories.filter((category) =>
      category.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    setFilteredCategories(results);
    setCurrentPage(1);
    setLoading(false);
  }, 1000); // small delay for better UX

  return () => clearTimeout(timeout);
}, [searchQuery, allCategories]);


  // single effect to route correctly
  useEffect(() => {
    const isEdit = routerLocation.pathname.includes("/edit");
    const isAdd = routerLocation.pathname.includes("/add");

    const handleRoute = async () => {
      // If adding a new category
      if (isAdd) {
        setShowCategoryList(false);
        setNewCategoryName("");
        setFormData({ photos: [] });
        setEditingCategoryId(null);
        return;
      }

      // If editing an existing category
      if (isEdit && categoryIdFromParams) {
        await fetchCategoryById(categoryIdFromParams, true);
        return;
      }

      // If viewing a specific category by ID (not editing)
      if (categoryIdFromParams) {
        await fetchCategoryById(categoryIdFromParams, false);
        return;
      }

      // No categoryId in URL => list view
      setFilteredCategories(allCategories);
      setShowCategoryList(true);
      setEditingCategoryId(null);
    };

    handleRoute();
  }, [routerLocation.pathname, categoryIdFromParams, allCategories]);

  const handleAddCategory = async () => {
    setTouched(true);

    if (!newCategoryName.trim() && !editingCategoryId) return;

    try {
      let response;

      const form = new FormData();
      form.append("name", newCategoryName.trim());
      form.append("status", "true"); // or "false" if inactive

      const existingPhotos: string[] = [];
      if (formData.photos && formData.photos.length > 0) {
        (formData.photos as (File | string)[]).forEach((item) => {
          if (item instanceof File) {
            form.append("files", item);
          } else if (typeof item === "string") {
            existingPhotos.push(item);
          }
        });
      }
      form.append("existing_photos", JSON.stringify(existingPhotos));

      if (editingCategoryId) {
        // 🛠 Update existing category
        response = await PatchApiMethod(
          `${CategoryEndpoint.CATEGORY_LIST_URL}/${editingCategoryId}`,
          form,
          // {
          //   headers: { "Content-Type": "multipart/form-data" },
          // }
        );

        const updatedCategory = response?.data?.data?.category;
        if (updatedCategory) {
          setAllCategories((prev) =>
            prev.map((cat) =>
              cat.id === editingCategoryId ? updatedCategory : cat
            )
          );
          navigate("/categories")
          showSuccess("Category updated successfully!");
        }
      } else {

        // Check for duplicate name
        const existingCategory = allCategories.find(
          (cat) =>
            cat.name.toLowerCase() === newCategoryName.trim().toLowerCase()
        );
        if (existingCategory) {
          showSuccess("Category already exists!");
          return;
        }

        response = await PostApiMethod(
          CategoryEndpoint.CATEGORY_LIST_URL,
          form,
          // {
          //   headers: { "Content-Type": "multipart/form-data" },
          // }
        );

        const newCategory = response?.data?.data;
        if (newCategory) {
          setAllCategories((prev) => [...prev, newCategory]);
          showSuccess("Category added successfully!");
        }
      }

      //  Sync with latest data from server
      await fetchCategories();
      resetForm();

    } catch (error) {
      console.error("POST/PATCH error:", error);
      showSuccess("Failed to add/update category.");
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    try {
      await DeleteApiMethod(`${CategoryEndpoint.CATEGORY_LIST_URL}/${categoryId}`);
      setAllCategories((prev) => prev.filter((cat) => cat.id !== categoryId));
      showSuccess("Category deleted successfully!");
    } catch (error) {
      console.error("Failed to delete category:", error);
      showSuccess("Failed to delete category.");
    }
  };

  // const handleDisable = (category: ContextCategory) => {
  //   setIsDisabled(true);
  //   handleCloseMenu();
  //   console.log("Disable category:", category.id);
  //   // TODO: call disable API if required
  // };

  const resetForm = () => {
    setEditingCategoryId(null);
    setNewCategoryName("");
    setFormData({ photos: [] });
    setShowCategoryList(true);
    setTouched(false);
    navigate("/categories");
  };

  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);
  const paginatedCategories = filteredCategories.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, categoryId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedCategoryId(categoryId);
  };
  const handleCloseMenu = () => setAnchorEl(null);

  const handleStatusToggle = async (selected: any) => {
    try {
      const newStatus = selected.status ? 0 : 1;

      await PatchApiMethod(`${CategoryEndpoint.CATEGORY_LIST_URL}/${selected.id}`, { status: newStatus });

      showSuccess(
        `Category "${selected.name}" ${newStatus ? "enabled" : "disabled"} successfully!`
      );

      await fetchCategories(); // refresh list
    } catch (err) {
      console.error("Error updating category status:", err);
      showSuccess("Failed to update category status.");
    } finally {
      handleCloseMenu();
    }
  };

  return (
    <AuthorizedLayout>
      <Box sx={{ px: 2, py: 3, fontFamily: "Poppins, Semibold" }}>
        {loading && (
          <Box className="loading-overlay">
            <CircularProgress size={50} thickness={5} />
          </Box>
        )}
        <Card className="card-layout" sx={{ overflow: "auto" }}>
          <BoxHeader
            dashboard_name="Categories"
            image={Icon}
            button_show={true}
            button_name="New Category"
            onAdd={() => {
              navigate(`/categories/add`);
            }}
            tabs={["All Categories"]}
            activeTab={tab}
            onTabChange={(newTab) => setTab(newTab)}
          />

          {!showCategoryList ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleAddCategory();
              }}
            >
              <Typography className="form-header" sx={{ mb: 3 }}>
                {editingCategoryId ? "Edit Category" : "Add New Category"}
              </Typography>

              <FormControl fullWidth variant="standard">
                <Typography className="form-input">Category</Typography>
                <TextField
                  variant="standard"
                  placeholder="Type here"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onBlur={() => setTouched(true)}
                  error={
                    touched &&
                    (newCategoryName.trim() === "" || newCategoryName.length === 255)
                  }
                  inputProps={{ maxLength: 255 }}
                />
                <FormHelperText error>
                  {touched && newCategoryName.trim() === ""
                    ? "Category field is mandatory"
                    : touched && newCategoryName.length === 255
                      ? "Max 255 characters reached"
                      : ""}
                </FormHelperText>
              </FormControl>

              <Box mt={4}>
                <CategoryPhotosForm
                  formData={formData}
                  setFormData={setFormData}
                  title="Category Images"
                />
              </Box>

              <Box mt={2} display="flex" gap={2}>
                <Button onClick={resetForm} className="btn-cancel" type="button">
                  Cancel
                </Button>
                <Button className="btn-next" type="submit" disabled={loading}>
                  {editingCategoryId ? "Update" : "Add"}
                </Button>
              </Box>
            </form>
          ) : (
            <>
              <SearchandExport
                searchPlaceholder="Type here"
                onSearchChange={setSearchQuery}
              />
              <Table className="table">
                <TableHead>
                  <TableRow className="table-border">
                    <TableCell>
                      <div className="inner-cell">
                        <Typography className="table-header">Category</Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="inner-cell">
                        <Typography className="table-header" align="center">
                          Category Image
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="inner-cell">
                        <Typography className="table-header">Action</Typography>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCategories.map((category, index) => (
                    <TableRow
                      className="table-border"
                      key={category.id}
                      sx={{
                        backgroundColor: category.status ? "inherit" : "#f5f5f5",
                      }}
                    >
                      <TableCell sx={{ opacity: category.status ? 1 : 0.5 }}>
                        <div className="inner-cell">
                          <Typography className="table-cell ">{category.name}</Typography>
                        </div>
                      </TableCell>
                      <TableCell sx={{ opacity: category.status ? 1 : 0.5 }}>
                        <div
                          className="inner-cell"
                          style={{ display: "flex", gap: "8px" }}
                        >
                          {category.imageUrl ? (
                            <img
                              src={category.imageUrl}
                              alt={category.name}
                              style={{
                                width: 50,
                                height: 50,
                                objectFit: "cover",
                                borderRadius: 4,
                              }}
                            />
                          ) : (
                            <Typography className="table-cell muted">
                              No Photo
                            </Typography>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="inner-cell">
                          <Box display="flex" alignItems="center">
                            <Typography className="table-cell action">Action</Typography>
                            <IconButton
                              onClick={(e) => handleOpenMenu(e, category.id)}
                            >
                              <img src={ActionIcon} alt="icon" />
                            </IconButton>
                          </Box>
                        </div>
                      </TableCell>
                    </TableRow>

                  ))}
                </TableBody>
              </Table>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                {(() => {
                  const selected = allCategories.find(
                    (c: ContextCategory) => c.id === selectedCategoryId
                  );
                  if (!selected) return null;

                  const isDisabled = !selected.status;

                  return (
                    <>
                      {!isDisabled && (
                        <>
                          <MenuItem
                            onClick={() => {
                              navigate(`/categories/${selected.id}/edit`);
                              handleCloseMenu();
                            }}
                            className="edit-menu-item"
                          >
                            Edit
                          </MenuItem>

                          <MenuItem
                            onClick={() => {
                              handleDeleteCategory(selected.id);
                              handleCloseMenu();
                            }}
                            className="delete-menu-item"
                          >
                            Delete
                          </MenuItem>
                        </>
                      )}

                      <MenuItem
                        onClick={() => handleStatusToggle(selected)}
                        className={selected.status ? "disable-menu-item" : "enable-menu-item"}
                      >
                        {selected.status ? "Disable" : "Enable"}
                      </MenuItem>
                    </>
                  );
                })()}
              </Menu>


              {filteredCategories.length === 0 && (
                <Typography className="centered-muted-text">
                  No categories found.
                </Typography>
              )}
              {filteredCategories.length > itemsPerPage && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, value) => {
                      setCurrentPage(value);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}

          <SuccessDialog
            open={successOpen}
            onClose={closeSuccess}
            message={message}
          />
        </Card>
      </Box>
    </AuthorizedLayout>
  );
};

export default Categories;
